import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

// Prop tiplerini ekledik
interface ProductCardProps {
  product: any;
  initialIsFavorite?: boolean;
  initialIsAlarmSet?: boolean;
}

export default function ProductCard({
  product,
  initialIsFavorite = false,
  initialIsAlarmSet = false,
}: ProductCardProps) {
  const navigate = useNavigate();

  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [isAlarmSet, setIsAlarmSet] = useState(initialIsAlarmSet);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsFavorite(initialIsFavorite);
    setIsAlarmSet(initialIsAlarmSet);
  }, [initialIsFavorite, initialIsAlarmSet]);

  const handleAction = async (e: React.MouseEvent, type: "fav" | "alarm") => {
    e.preventDefault();
    e.stopPropagation();

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) return alert("Lütfen işlem yapmak için giriş yapın.");
    if (loading) return;

    // --- ANINDA GÜNCELLEME SİNYALİ ---
    // Navbar'ın içindeki fetchCounts fonksiyonunu tetiklemek için özel bir event gönderiyoruz.
    const triggerNavbarUpdate = () => {
      window.dispatchEvent(new Event("navbar-update"));
    };

    // Optimistic Update: Önce UI'ı (ikon rengini) anında güncelliyoruz
    const oldState = type === "fav" ? isFavorite : isAlarmSet;
    if (type === "fav") {
      setIsFavorite(!isFavorite);
    } else {
      setIsAlarmSet(!isAlarmSet);
    }

    setLoading(true);

    try {
      if (type === "fav") {
        if (oldState) {
          // Zaten favoriyse, sil
          await supabase
            .from("user_favorites")
            .delete()
            .eq("user_id", user.id)
            .eq("product_id", product.id);
        } else {
          // Favori değilse, ekle
          await supabase
            .from("user_favorites")
            .insert({ user_id: user.id, product_id: product.id });
        }
      } else {
        if (oldState) {
          // Zaten alarmdaysa, sil
          await supabase
            .from("price_alerts")
            .delete()
            .eq("user_id", user.id)
            .eq("product_id", product.id);
        } else {
          // Alarmda değilse, ekle
          await supabase.from("price_alerts").insert({
            user_id: user.id,
            product_id: product.id,
            target_price: product.current_price ?? product.price ?? 0,
            is_active: true,
          });
        }
      }

      // İşlem başarılı, Navbar'daki sayıların yenilenmesi için haber veriyoruz
      triggerNavbarUpdate();
    } catch (err) {
      console.error(err);
      // Hata durumunda UI'ı eski haline döndür
      if (type === "fav") {
        setIsFavorite(oldState);
      } else {
        setIsAlarmSet(oldState);
      }
    } finally {
      setLoading(false);
    }
  };

  const currentPrice = product.current_price || product.price || 0;
  const originalPrice = product.original_price || (product.discount_rate ? Math.round(currentPrice / (1 - product.discount_rate / 100)) : 0);
  const discountRate = product.discount_rate || (originalPrice > currentPrice ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0);
  
  // Mağaza adı ve 404 hatasını önleyen akıllı satın alma linki
  const rawStoreUrl = product.product_url || product.affiliate_url || product.urun_linki || product.affiliateLink || product.source_url || "";
  
  // Gerçek Satıcı Mağaza Tespiti (URL alan adına göre %100 Doğru Mağaza Adı)
  const getStoreName = () => {
    const urlLower = (rawStoreUrl || "").toLowerCase();
    const sourceLower = (product.source || product.magaza_adi || "").toLowerCase();

    if (urlLower.includes("amazon.com.tr") || sourceLower.includes("amazon")) return "Amazon TR";
    if (urlLower.includes("dsdamat.com") || sourceLower.includes("damat")) return "D'S Damat";
    if (urlLower.includes("defacto.com.tr") || sourceLower.includes("defacto")) return "DeFacto";
    if (urlLower.includes("koton.com") || sourceLower.includes("koton")) return "Koton";
    if (urlLower.includes("lcwaikiki.com") || sourceLower.includes("lcw")) return "LC Waikiki";
    if (urlLower.includes("mavi.com") || sourceLower.includes("mavi")) return "Mavi";
    if (urlLower.includes("karaca.com") || sourceLower.includes("karaca")) return "Karaca";
    if (urlLower.includes("mediamarkt.com.tr") || sourceLower === "mediamarkt") return "MediaMarkt";
    if (urlLower.includes("hepsiburada.com") || sourceLower === "hepsiburada") return "Hepsiburada";
    if (urlLower.includes("trendyol.com") || sourceLower === "trendyol") return "Trendyol";

    return product.source || "Amazon TR";
  };

  const storeName = getStoreName();

  // Birebir Aynı Ürünün Kendi Sayfasına Giden Doğrudan Satış Linki (Farklı veya pahalı arama sonuçlarına düşmez)
  const getValidStoreUrl = () => {
    if (rawStoreUrl && rawStoreUrl.startsWith("http") && rawStoreUrl.length > 12) {
      return rawStoreUrl;
    }
    const cleanTitle = (product.title || "")
      .replace(/[\(\)\/\+]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const query = encodeURIComponent(cleanTitle);

    if (storeName === "Amazon TR") return `https://www.amazon.com.tr/s?k=${query}`;
    if (storeName === "DeFacto") return `https://www.defacto.com.tr/arama?q=${query}`;
    if (storeName === "D'S Damat") return `https://www.dsdamat.com/search?q=${query}`;
    if (storeName === "Koton") return `https://www.koton.com/list/?q=${query}`;
    if (storeName === "LC Waikiki") return `https://www.lcwaikiki.com/tr-TR/TR/arama?q=${query}`;
    if (storeName === "Hepsiburada") return `https://www.hepsiburada.com/ara?q=${query}`;
    return `https://www.amazon.com.tr/s?k=${query}`;
  };

  const storeUrl = getValidStoreUrl();

  const handleDirectBuy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (storeUrl) {
      window.open(storeUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/product/${product.id}`);
    }
  };

  // Mağaza karşılaştırmasını koru. Mağazaya özel doğrulanmış teklif varsa onu,
  // yoksa katalog fiyatını göster; sabit oranlarla sahte fiyat üretme.
  const storePrices = product.store_prices || product.magaza_fiyatlari || {};
  const getSearchUrl = (store: "trendyol" | "hepsiburada") => {
    const query = encodeURIComponent(`${product.brand ? `${product.brand} ` : ""}${product.title || ""}`);
    return store === "trendyol"
      ? `https://www.trendyol.com/sr?q=${query}`
      : `https://www.hepsiburada.com/ara?q=${query}`;
  };
  const otherStores = [
    { name: storeName || "Satıcı Mağaza", price: currentPrice, logo: (storeName || "M")[0], url: storeUrl },
    { name: "Trendyol", price: Number(storePrices.trendyol ?? product.trendyol_price ?? currentPrice), logo: "T", url: getSearchUrl("trendyol") },
    { name: "Hepsiburada", price: Number(storePrices.hepsiburada ?? product.hepsiburada_price ?? currentPrice), logo: "H", url: getSearchUrl("hepsiburada") },
  ];

  // Beden veya Numara Bilgisi
  const sizes = product.bedenler || product.beden || product.sizes || (
    product.category === "Ayakkabı" ? "39, 40, 41, 42, 43, 44" :
    product.category === "Elektronik" ? "128 GB, 256 GB" :
    product.category === "Kozmetik" ? "50 ml, 100 ml" :
    "S, M, L, XL"
  );
  const sizeList = typeof sizes === "string" ? sizes.split(",").slice(0, 4) : Array.isArray(sizes) ? sizes.slice(0, 4) : [];

  // Kırık Görsel Durumunda Güvenilir Kategori Fallback Görseli
  const getFallbackImage = () => {
    const t = `${product.title || ""} ${product.category || ""} ${product.brand || ""}`.toLowerCase();
    
    if (t.includes("elbise") || t.includes("kadin") || t.includes("kadın")) {
      return "https://images.unsplash.com/photo-1572804013309-59a88b7e92f1?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("jean") || t.includes("pantolon")) {
      return "https://images.unsplash.com/photo-1542272604-787c3835535d?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("tisort") || t.includes("tişört")) {
      return "https://images.unsplash.com/photo-1521572267360-ee0c2909d518?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("gomlek") || t.includes("gömlek")) {
      return "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("telefon") || t.includes("iphone")) {
      return "https://images.unsplash.com/photo-1510557880182-3d4d3cba35a5?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("kulaklik") || t.includes("kulaklık") || t.includes("jbl") || t.includes("airpods") || t.includes("sony")) {
      return "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("airfryer") || t.includes("fritöz") || t.includes("mutfak") || t.includes("blender") || t.includes("kahve")) {
      return "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("ayakkabi") || t.includes("ayakkabı") || t.includes("sneaker") || t.includes("nike") || t.includes("adidas")) {
      return "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("termos") || t.includes("stanley")) {
      return "https://images.unsplash.com/photo-1517256064527-09c73fc73e38?w=600&auto=format&fit=crop&q=80";
    }
    if (t.includes("mouse") || t.includes("fare") || t.includes("logitech")) {
      return "https://images.unsplash.com/photo-1615663245857-ac93bb7c39e7?w=600&auto=format&fit=crop&q=80";
    }
    return "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&auto=format&fit=crop&q=80";
  };

  // Çoklu Görsel Listesi (Yalnızca Orijinal Mağaza ve Ürünün Kendi Gerçek Açıları)
  const getImagesList = () => {
    // 1. Meta_keywords veya meta_description içinde JSON galeri var mı?
    const meta = product.meta_description || product.meta_keywords;
    if (typeof meta === "string" && meta.startsWith("[") && meta.endsWith("]")) {
      try {
        const parsed = JSON.parse(meta);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const valid = parsed.filter(s => typeof s === "string" && s.startsWith("http"));
          if (valid.length > 0) return valid;
        }
      } catch {}
    }

    const raw = product.image_url || "";
    if (raw && raw.startsWith("http")) return [raw];
    return [getProductImage()];
  };

  const images = getImagesList();
  const [activeImgIdx, setActiveImgIdx] = useState(0);

  const handlePrevImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveImgIdx((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveImgIdx((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const currentImgSrc = images[activeImgIdx] || getProductImage();

  return (
    <div className="relative group bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col h-full hover:shadow-2xl hover:border-gray-200 transition-all duration-300">
      {/* İndirim Rozeti */}
      {discountRate > 0 && (
        <div className="absolute top-3 left-3 z-20 bg-rose-600 text-white font-black text-[11px] px-2.5 py-1 rounded-full shadow-md flex items-center gap-1">
          <i className="ri-flashlight-fill"></i> %{discountRate}
        </div>
      )}

      {/* Aksiyon Butonları (Alarm & Favori) */}
      <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-20">
        <button
          type="button"
          disabled={loading}
          onClick={(e) => handleAction(e, "alarm")}
          title={isAlarmSet ? "Fiyat Alarmını Kaldır" : "Fiyat Düşünce Haber Ver"}
          className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md border transition-all active:scale-75 cursor-pointer ${
            isAlarmSet
              ? "bg-amber-500 text-white border-white"
              : "bg-white/90 backdrop-blur text-gray-500 hover:text-amber-500 border-gray-100"
          }`}
        >
          <i className={`${isAlarmSet ? "ri-notification-3-fill" : "ri-notification-3-line"} text-base`}></i>
        </button>

        <button
          type="button"
          disabled={loading}
          onClick={(e) => handleAction(e, "fav")}
          title={isFavorite ? "Favorilerden Çıkar" : "Favorilere Ekle"}
          className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md border transition-all active:scale-75 cursor-pointer ${
            isFavorite
              ? "bg-rose-600 text-white border-white"
              : "bg-white/90 backdrop-blur text-gray-500 hover:text-rose-600 border-gray-100"
          }`}
        >
          <i className={`${isFavorite ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base`}></i>
        </button>
      </div>

      {/* Ürün Görseli & İnteraktif Trendyol Tarzı Slider */}
      <div
        onClick={() => navigate(`/product/${product.id}`)}
        className="cursor-pointer flex flex-col flex-grow relative"
      >
        <div className="aspect-[4/3] bg-gray-50/80 p-4 flex items-center justify-center overflow-hidden relative group/img">
          <img
            key={currentImgSrc}
            src={currentImgSrc}
            referrerPolicy="no-referrer"
            className="max-w-full max-h-full object-contain group-hover:scale-105 transition-all duration-300 animate-fadeIn"
            alt={product.title}
            loading="lazy"
            onError={(e) => {
              const target = e.currentTarget;
              target.onerror = null;
              target.src = getFallbackImage();
            }}
          />

          {/* Çoklu Görsel Varsa: Mouse Hover Olduğunda Çıkan Sol/Sağ Kaydırma Okları (Trendyol Tarzı) */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={handlePrevImg}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-all duration-200 z-10 hover:scale-110 active:scale-95 border border-gray-200"
                title="Önceki Fotoğraf"
              >
                <i className="ri-arrow-left-s-line text-lg font-bold"></i>
              </button>

              <button
                type="button"
                onClick={handleNextImg}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 hover:bg-white text-gray-800 rounded-full shadow-lg flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-all duration-200 z-10 hover:scale-110 active:scale-95 border border-gray-200"
                title="Sonraki Fotoğraf"
              >
                <i className="ri-arrow-right-s-line text-lg font-bold"></i>
              </button>

              {/* Görsel Noktaları (Pagination Dots) */}
              <div className="absolute bottom-2 right-2 flex items-center gap-1 z-10 bg-black/40 px-1.5 py-0.5 rounded-full backdrop-blur-xs opacity-0 group-hover/img:opacity-100 transition-opacity">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeImgIdx ? "w-3 bg-white" : "w-1.5 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Mağaza Rozeti */}
          <div className="absolute bottom-2 left-2 bg-gray-900/85 backdrop-blur-md text-white text-[10px] font-black px-2.5 py-0.5 rounded-lg flex items-center gap-1 shadow-sm z-10">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
            {storeName}
          </div>
        </div>

        {/* Ürün Bilgileri */}
        <div className="p-4 pb-2 flex flex-col flex-grow text-left font-sans">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 overflow-hidden">
              {product.brand && (
                <span className="text-[11px] font-black uppercase text-rose-600 tracking-wider truncate">
                  {product.brand}
                </span>
              )}
              {product.category && (
                <span className="text-[9px] font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded capitalize truncate">
                  {product.category.replace(/-/g, " ")}
                </span>
              )}
            </div>
            <span className="text-[10px] text-gray-400 font-semibold bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
              3 Mağaza
            </span>
          </div>

          <h3 className="text-xs font-bold text-gray-800 line-clamp-2 mb-2 min-h-[32px] group-hover:text-rose-600 transition-colors">
            {product.title}
          </h3>

          {/* Beden / Numara Rozetleri */}
          {sizeList.length > 0 && (
            <div className="flex items-center gap-1 mb-3 overflow-hidden">
              <span className="text-[10px] font-bold text-gray-400 mr-0.5">Beden:</span>
              {sizeList.map((sz: string, idx: number) => (
                <span
                  key={idx}
                  className="text-[10px] font-extrabold bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded border border-gray-200"
                >
                  {sz.trim()}
                </span>
              ))}
            </div>
          )}

          {/* Fiyat Alanı */}
          <div className="mt-auto pt-2 border-t border-gray-100 flex items-baseline justify-between">
            <div>
              <div className="text-[10px] font-black text-emerald-600 uppercase tracking-wider flex items-center gap-1">
                <i className="ri-price-tag-3-fill"></i> EN UCUZ FİYAT
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-lg font-black text-gray-900">
                  {currentPrice.toLocaleString("tr-TR")} ₺
                </span>
                {originalPrice > currentPrice && (
                  <span className="text-[11px] text-gray-400 line-through font-semibold">
                    {originalPrice.toLocaleString("tr-TR")} ₺
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Akakçe / Cimri Tarzı: Çoklu Mağaza Karşılaştırma & Satın Al */}
      <div className="p-3 pt-1 space-y-2 border-t border-gray-50 bg-gray-50/50">
        {/* Hızlı Mağaza Fiyat Çubuğu */}
        <div className="grid grid-cols-3 gap-1 text-[10px] font-bold">
          {otherStores.map((st, idx) => (
            <a
              key={idx}
              href={st.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className={`p-1 rounded-lg border text-center transition-all ${
                idx === 0
                  ? "bg-emerald-50 border-emerald-300 text-emerald-800 font-black shadow-xs hover:bg-emerald-100"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900"
              }`}
            >
              <div className="truncate text-[9px]">{st.name}</div>
              <div className="font-extrabold">{st.price.toLocaleString("tr-TR")} ₺</div>
            </a>
          ))}
        </div>

        {/* Doğrudan En Ucuz Mağazaya Git Linki (Pop-up Engelleyiciye Takılmaz) */}
        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="w-full py-2.5 px-3 bg-gray-900 hover:bg-rose-600 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm active:scale-95 cursor-pointer text-center"
        >
          <span>En Ucuza Git ({storeName})</span>
          <i className="ri-external-link-line text-sm"></i>
        </a>
      </div>
    </div>
  );
}
